import { trpc } from '@/lib/trpc';
import { format } from 'date-fns';
import { DateRange, Granularity, QAReportStats } from '../types';
import { useOrganization } from '@/contexts/OrganizationContext';

interface UseQAReportStatsOptions {
  /**
   * Historical/comparison data that won't change — cache more aggressively
   * (30 min staleTime). Set to true for comparison-period queries.
   */
  isHistorical?: boolean;
  /**
   * Auto-refresh interval in ms. Default is no polling (matches the
   * Reports page's pull-only behaviour). The Reports TV view passes 60_000
   * so the print-room scoreboard stays current.
   */
  pollIntervalMs?: number;
}

export function useQAReportStats(
  locationId: string | undefined,
  dateRange: DateRange,
  granularity: Granularity,
  enabled = true,
  options?: UseQAReportStatsOptions
) {
  const { organization } = useOrganization();
  const isHistorical = options?.isHistorical ?? false;
  const pollIntervalMs = options?.pollIntervalMs;

  return trpc.qaReports.getQAReportStats.useQuery(
    {
      locationId: locationId ?? '',
      // Calendar dates in the user's browser calendar, interpreted as
      // location-local dates by the RPC. Prevents browser-vs-location TZ
      // drift from expanding a single-day pick into two days.
      startDate: format(dateRange.from, 'yyyy-MM-dd'),
      endDate: format(dateRange.to, 'yyyy-MM-dd'),
      granularity,
    },
    {
      enabled: Boolean(organization?.id) && Boolean(locationId) && enabled,
      staleTime: isHistorical ? 30 * 60 * 1000 : 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchInterval: pollIntervalMs ?? false,
    }
  );
}

export function useExportQAReport() {
  return trpc.qaReports.exportQAReport.useMutation();
}

/**
 * Shopify demand stats (orders placed + units ordered) for the capacity card.
 * Sends calendar dates (YYYY-MM-DD) interpreted in the location's timezone by
 * the RPC, exactly like useQAReportStats, so demand lines up with throughput.
 */
export function useQADemandStats(
  locationId: string | undefined,
  dateRange: DateRange,
  enabled = true
) {
  const { organization } = useOrganization();
  return trpc.qaReports.getDemandStats.useQuery(
    {
      locationId: locationId ?? '',
      startDate: format(dateRange.from, 'yyyy-MM-dd'),
      endDate: format(dateRange.to, 'yyyy-MM-dd'),
    },
    {
      enabled: Boolean(organization?.id) && Boolean(locationId) && enabled,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
}

// Calculate trend — uses comparison period data when available, otherwise half-splits current period
export function calculateTrend(
  data: QAReportStats | undefined,
  comparisonData?: QAReportStats | undefined
): {
  direction: 'improving' | 'worsening' | 'stable';
  percent: number;
} {
  if (!data) return { direction: 'stable', percent: 0 };

  // When comparison data is available, compare period-level averages
  if (comparisonData && comparisonData.defectTrend.length > 0) {
    const currentAvg = data.defectTrend.reduce((s, p) => s + p.defectRate, 0)
      / Math.max(data.defectTrend.length, 1);
    const prevAvg = comparisonData.defectTrend.reduce((s, p) => s + p.defectRate, 0)
      / Math.max(comparisonData.defectTrend.length, 1);
    if (prevAvg === 0) return { direction: 'stable', percent: 0 };
    const pct = ((currentAvg - prevAvg) / prevAvg) * 100;
    if (Math.abs(pct) < 5) return { direction: 'stable', percent: 0 };
    return { direction: pct < 0 ? 'improving' : 'worsening', percent: Math.abs(pct) };
  }

  // Fallback: half-split of current period
  if (data.defectTrend.length < 2) {
    return { direction: 'stable', percent: 0 };
  }

  const midpoint = Math.floor(data.defectTrend.length / 2);
  const firstHalf = data.defectTrend.slice(0, midpoint);
  const secondHalf = data.defectTrend.slice(midpoint);

  const firstAvg = firstHalf.reduce((sum, p) => sum + p.defectRate, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((sum, p) => sum + p.defectRate, 0) / secondHalf.length;

  if (firstAvg === 0) {
    return { direction: 'stable', percent: 0 };
  }

  const percentChange = ((secondAvg - firstAvg) / firstAvg) * 100;

  if (Math.abs(percentChange) < 5) {
    return { direction: 'stable', percent: 0 };
  }

  return {
    direction: percentChange < 0 ? 'improving' : 'worsening',
    percent: Math.abs(percentChange),
  };
}
